import React, { useState, useEffect, useRef } from 'react';
import { getContacts, createContact, uploadContacts, deleteContact, bulkAddTag } from '../api';
import TagsInput from '../components/TagsInput';
import toast from 'react-hot-toast';
import { Users, Plus, Upload, Trash2, Search, X, FileSpreadsheet, Tag } from 'lucide-react';

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [allTags, setAllTags] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [selected, setSelected] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ email:'',name:'',company:'',phone:'',tags:[],notes:'' });
  const [uploadTags, setUploadTags] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [bulkTag, setBulkTag] = useState('');
  const fileRef = useRef();

  useEffect(() => { load(); }, [page, search, tagFilter]);
  const load = async () => {
    try {
      const r = (await getContacts({ page, limit:100, search, tag:tagFilter })).data;
      setContacts(r.contacts); setTotal(r.total); setAllTags(r.allTags||[]);
    } catch(e){}
  };

  const handleAdd = async () => {
    if (!form.email) return toast.error('Email required');
    try { await createContact(form); toast.success('Added!'); setModal(null); setForm({email:'',name:'',company:'',phone:'',tags:[],notes:''}); load(); }
    catch(e){ toast.error(e.response?.data?.error||'Failed'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('tags', JSON.stringify(uploadTags));
    try {
      const r = (await uploadContacts(fd)).data;
      setUploadResult(r);
      toast.success(`Imported ${r.imported} contacts!`);
      load();
    } catch(e) { toast.error(e.response?.data?.error||'Upload failed'); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleBulkTag = async () => {
    if (!bulkTag || selected.length===0) return toast.error('Select contacts and enter a tag');
    try { await bulkAddTag({contactIds:selected,tag:bulkTag.trim().toLowerCase()}); toast.success(`Tagged ${selected.length} contacts`); setSelected([]); setBulkTag(''); load(); }
    catch(e){ toast.error('Failed'); }
  };

  const toggleSelect = (id) => setSelected(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);
  const selectAll = () => setSelected(selected.length===contacts.length?[]:contacts.map(c=>c._id));

  return (<>
    <div className="pg-hdr">
      <h2>Contacts</h2>
      <p>{total} contacts • {allTags.length} tags</p>
      <div className="pg-acts">
        <button className="btn btn-p" onClick={()=>setModal('add')}><Plus size={15}/> Add Contact</button>
        <button className="btn btn-s" onClick={()=>setModal('upload')}><Upload size={15}/> Bulk Import</button>
        {selected.length>0 && <button className="btn btn-s" onClick={()=>setModal('bulkTag')}><Tag size={15}/> Tag {selected.length} selected</button>}
      </div>
    </div>
    <div className="pg-body">
      {/* Search + Tag Filter */}
      <div className="card" style={{marginBottom:14,display:'flex',alignItems:'center',gap:10,padding:12,flexWrap:'wrap'}}>
        <Search size={15} style={{color:'var(--txtm)'}}/>
        <input className="inp" style={{border:'none',background:'transparent',padding:0,flex:1,minWidth:150}} placeholder="Search..." value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}/>
        {search&&<button className="btn btn-g btn-sm" onClick={()=>setSearch('')}><X size={13}/></button>}
        <div style={{borderLeft:'1px solid var(--bdr)',height:24,margin:'0 6px'}}/>
        <select className="inp sel" style={{width:'auto',minWidth:130,padding:'6px 28px 6px 10px',fontSize:12}} value={tagFilter} onChange={e=>{setTagFilter(e.target.value);setPage(1);}}>
          <option value="">All Tags</option>
          {allTags.map(t=><option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div className="card">
        {contacts.length===0 ? <div className="empty"><Users size={44}/><h3>No contacts</h3><p>Add contacts or import from CSV/Excel</p></div> : (
          <div style={{overflowX:'auto'}}>
            <table>
              <thead><tr>
                <th style={{width:36}}><input type="checkbox" checked={selected.length===contacts.length&&contacts.length>0} onChange={selectAll}/></th>
                <th>Email</th><th>Name</th><th>Company</th><th>Tags</th><th></th>
              </tr></thead>
              <tbody>{contacts.map(c=>(
                <tr key={c._id}>
                  <td><input type="checkbox" checked={selected.includes(c._id)} onChange={()=>toggleSelect(c._id)}/></td>
                  <td style={{fontFamily:'var(--mono)',fontSize:11.5,color:'var(--txt)'}}>{c.email}</td>
                  <td>{c.name||'—'}</td>
                  <td>{c.company||'—'}</td>
                  <td>{c.tags?.map((t,i)=><span className="tag" key={i}>{t}</span>)}</td>
                  <td><button className="btn btn-g btn-sm" onClick={async()=>{if(!window.confirm('Delete?'))return;await deleteContact(c._id);toast.success('Deleted');load();}}><Trash2 size={13}/></button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {total>100&&<div style={{display:'flex',justifyContent:'center',gap:8,marginTop:14}}>
          <button className="btn btn-g btn-sm" disabled={page===1} onClick={()=>setPage(p=>p-1)}>Prev</button>
          <span style={{fontSize:11,color:'var(--txtm)',lineHeight:'30px'}}>Page {page}/{Math.ceil(total/100)}</span>
          <button className="btn btn-g btn-sm" disabled={contacts.length<100} onClick={()=>setPage(p=>p+1)}>Next</button>
        </div>}
      </div>
    </div>

    {/* Add Contact */}
    {modal==='add'&&<div className="modal-bg" onClick={()=>setModal(null)}><div className="modal" onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr"><h3>Add Contact</h3><button className="btn btn-g btn-sm" onClick={()=>setModal(null)}><X size={15}/></button></div>
      <div className="modal-body">
        <div className="fg"><label className="lbl">Email *</label><input className="inp" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="john@company.com"/></div>
        <div className="fr2">
          <div className="fg"><label className="lbl">Name</label><input className="inp" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="John Doe"/></div>
          <div className="fg"><label className="lbl">Company</label><input className="inp" value={form.company} onChange={e=>setForm({...form,company:e.target.value})} placeholder="Acme Inc"/></div>
        </div>
        <div className="fg"><label className="lbl">Tags</label><TagsInput tags={form.tags} onChange={tags=>setForm({...form,tags})}/></div>
      </div>
      <div className="modal-ft"><button className="btn btn-s" onClick={()=>setModal(null)}>Cancel</button><button className="btn btn-p" onClick={handleAdd}>Add</button></div>
    </div></div>}

    {/* Bulk Upload */}
    {modal==='upload'&&<div className="modal-bg" onClick={()=>{setModal(null);setUploadResult(null);}}><div className="modal" onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr"><h3>Bulk Import</h3><button className="btn btn-g btn-sm" onClick={()=>{setModal(null);setUploadResult(null);}}><X size={15}/></button></div>
      <div className="modal-body">
        <p style={{fontSize:12.5,color:'var(--txt2)',marginBottom:12}}>Upload CSV/Excel with columns: <strong>email</strong>, <strong>name</strong>, <strong>company</strong>, <strong>tags</strong> (comma-separated)</p>
        <div className="fg"><label className="lbl">Auto-tag all imported contacts</label><TagsInput tags={uploadTags} onChange={setUploadTags} placeholder="e.g. lead, q1-2026"/></div>
        <input type="file" ref={fileRef} accept=".csv,.xlsx,.xls" style={{display:'none'}} onChange={handleUpload}/>
        <div className="upload-zone" onClick={()=>fileRef.current?.click()}>
          <FileSpreadsheet size={32} style={{color:'var(--txtm)',marginBottom:6}}/>
          <p style={{fontSize:12.5,color:'var(--txt2)'}}>Click to select CSV or Excel file</p>
        </div>
        {uploadResult&&<div style={{marginTop:14,padding:14,background:'var(--okD)',borderRadius:'var(--rs)',border:'1px solid rgba(0,214,143,0.2)'}}>
          <p style={{fontSize:12.5,color:'var(--ok)',fontWeight:600}}>Import Complete!</p>
          <p style={{fontSize:11.5,color:'var(--txt2)',marginTop:3}}>{uploadResult.imported} processed • {uploadResult.created} new • {uploadResult.updated} updated</p>
        </div>}
      </div>
    </div></div>}

    {/* Bulk Tag */}
    {modal==='bulkTag'&&<div className="modal-bg" onClick={()=>setModal(null)}><div className="modal" onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr"><h3>Tag {selected.length} Contacts</h3><button className="btn btn-g btn-sm" onClick={()=>setModal(null)}><X size={15}/></button></div>
      <div className="modal-body">
        <div className="fg"><label className="lbl">Tag name</label><input className="inp" value={bulkTag} onChange={e=>setBulkTag(e.target.value)} placeholder="e.g. vip, prospect"/></div>
      </div>
      <div className="modal-ft"><button className="btn btn-p" onClick={handleBulkTag}><Tag size={15}/> Apply Tag</button></div>
    </div></div>}
  </>);
}
